import { inject, Injectable } from '@angular/core';
import {
    ConfirmDialog,
    ConfirmDialogStatus,
    DialogManager,
} from 'conversai-components';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';

/** Opções para exibir um diálogo de confirmação genérico. */
export type ConfirmOptions = {
    /** Título do diálogo. */
    title: string;
    /** Mensagem do corpo do diálogo. */
    message: string;
    /** Rótulo do botão de confirmar (padrão: "Confirmar"). */
    confirmButtonLabel?: string;
    /** Rótulo do botão de cancelar (padrão: "Cancelar"). */
    cancelButtonLabel?: string;
    /**
     * Variante de cor do botão de confirmar. Use `'danger'` (vermelho) em
     * confirmações destrutivas, como remoções/exclusões. Padrão: `'default'`.
     */
    confirmButtonVariant?: 'default' | 'primary' | 'danger';
    /** Variante de cor do botão de cancelar. Padrão: `'default'`. */
    cancelButtonVariant?: 'default' | 'primary' | 'danger';
    /** Ícone (Font Awesome) opcional do botão de cancelar. */
    cancelButtonIcon?: string;
    /** Ícone (Font Awesome) opcional do botão de confirmar. */
    confirmButtonIcon?: string;
    /** Quando `true`, o botão de cancelar recebe o foco ao abrir. */
    focusCancelOnOpen?: boolean;
    /** Largura do diálogo (opcional). */
    width?: string;
    /** Altura do diálogo (opcional). */
    height?: string;
};

/**
 * Serviço central para diálogos de confirmação.
 *
 * Centraliza o boilerplate de abrir um {@link ConfirmDialog} via
 * {@link DialogManager}, evitando a repetição que existia nos diversos serviços
 * `Dialogs` de cada feature. Use {@link confirm} para confirmações genéricas.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
    private readonly transloco = inject(TranslocoService);

    constructor(private readonly dialogManager: DialogManager) {}

    /**
     * Exibe um diálogo de confirmação e retorna o status escolhido.
     *
     * @param options Configuração do diálogo (título, mensagem, rótulos, tamanho).
     * @returns Observable que emite o {@link ConfirmDialogStatus} ao fechar.
     */
    public confirm(options: ConfirmOptions): Observable<ConfirmDialogStatus> {
        return this.dialogManager
            .show({
                component: ConfirmDialog,
                ...(options.width ? { width: options.width } : {}),
                ...(options.height ? { height: options.height } : {}),
                inputs: {
                    title: options.title,
                    message: options.message,
                    cancelButtonLabel:
                        options.cancelButtonLabel ??
                        this.transloco.translate('common.actions.cancel'),
                    confirmButtonLabel:
                        options.confirmButtonLabel ??
                        this.transloco.translate('common.actions.confirm'),
                    confirmButtonVariant:
                        options.confirmButtonVariant ?? 'default',
                    cancelButtonVariant:
                        options.cancelButtonVariant ?? 'default',
                    cancelButtonIcon: options.cancelButtonIcon ?? null,
                    confirmButtonIcon: options.confirmButtonIcon ?? null,
                    focusCancelOnOpen: options.focusCancelOnOpen ?? false,
                },
            })
            .afterClosed();
    }

    /**
     * Confirmação **padrão de exclusão**, unificando a aparência em todas as
     * áreas (conta, organização, administração):
     * - título fixo "Confirmar Exclusão";
     * - botão de desistir (cancelar) azul claro, com ícone de proibição;
     * - botão de confirmar em vermelho ("Confirmar Exclusão") com ícone de lixeira;
     * - foco inicial no botão de desistir (ação segura por padrão).
     *
     * A mensagem específica de cada exclusão é passada por `message`.
     *
     * @param options Configuração (mensagem obrigatória; tamanho opcional).
     * @returns Observable que emite o {@link ConfirmDialogStatus} ao fechar.
     */
    public confirmDeletion(options: {
        message: string;
        width?: string;
        height?: string;
    }): Observable<ConfirmDialogStatus> {
        return this.confirm({
            title: this.transloco.translate('common.deletion.title'),
            message: options.message,
            cancelButtonLabel: this.transloco.translate('common.deletion.cancel'),
            confirmButtonLabel: this.transloco.translate(
                'common.deletion.confirm',
            ),
            confirmButtonVariant: 'danger',
            cancelButtonIcon: 'fa-solid fa-ban',
            confirmButtonIcon: 'fa-solid fa-trash',
            focusCancelOnOpen: true,
            width: options.width,
            height: options.height,
        });
    }

    /**
     * Confirmação padrão de cancelamento de operação (perda de alterações).
     * Reutilizada por várias telas que antes duplicavam este diálogo.
     */
    public cancelOperation(): Observable<ConfirmDialogStatus> {
        return this.confirm({
            title: this.transloco.translate('dialog.cancelOperation.title'),
            message: this.transloco.translate('dialog.cancelOperation.message'),
            cancelButtonLabel: this.transloco.translate('common.actions.no'),
            confirmButtonLabel: this.transloco.translate('common.actions.yes'),
        });
    }
}
